import { Component, EventEmitter, Input, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { debounceTime } from 'rxjs/operators';
import { ObservableHelper } from 'src/app/helpers/observable-helper';
import Asset from 'src/app/model/asset';
import Company from 'src/app/model/company/company';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import { ClientPendingDetailSubProject } from 'src/app/model/project/client-pending-detail-sub-project';
import { Project } from 'src/app/model/project/project';
import { SubProject } from 'src/app/model/project/sub-project';
import { OnView } from 'src/app/model/tabs/on-view';
import { OnViewUpdatable } from 'src/app/model/tabs/on-view-updatable/on-view-updatable';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { RequestService } from 'src/app/services/request.service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { formatFirstProduct } from '../../projects/helpers/first-product/first-product-helper';

@Component({
  selector: 'app-recup-job-detail',
  templateUrl: './recup-job-detail.component.html',
  styleUrls: ['./recup-job-detail.component.scss'],
})
export class RecupJobDetailComponent
  extends SubscriptionBasedComponent
  implements OnView<string>
{
  @Input()
  subProjectId: number;

  @Input()
  projectId: number;

  detail: ClientPendingDetailSubProject;
  placementAssets: Asset[] = [];
  ipBoardExamples: Asset[] = [];
  executionAssets: Asset[] = [];
  designPictures: Asset[] = [];
  viewed: boolean = false;
  loading: boolean = false;
  componentsExecuted: boolean = false;

  selectedCompany: Company;

  onViewUpdatable: OnViewUpdatable<string>;

  @ViewChild(MatStepper)
  matHorizontalStepper: MatStepper;

  project: Project;
  subProject: SubProject;
  showAddProduct = true;

  save: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private clientCompanySelectService: ClientCompanySelectService,
    private requestService: RequestService,
    private snackService: SnackService,
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  onView(onViewUpdatable?: OnViewUpdatable<string>): void {
    this.viewed = true;

    this.onViewUpdatable = onViewUpdatable;

    if (this.detail == null) {
      this.fetchDetail();
      ObservableHelper.untilFirstNonNull(
        this.clientCompanySelectService.getSelectionChangedSubscribable()
      )
        .toPromise()
        .then((company) => (this.selectedCompany = company));
    }

    this.addSubscription(this.save.pipe(debounceTime(250)), () => {
      this.saveChanges();
    });
  }

  advanceStepper() {
    this.matHorizontalStepper.next();
    window.scrollTo(0, 0);
  }

  private fetchDetail(): void {
    this.loading = true;

    this.addSubscription(
      this.requestService.getNormal('/projects/' + this.projectId),
      (res) => {
        this.initProject(res);
      }
    );
  }

  private initProject(project: Project) {
    this.loading = true;
    if (project.subProjects == null) {
      this.snackService.showError('Project heeft geen subprojecten');
      return;
    }
    this.project = project;
    let initSubProject: SubProject = null;
    for (let subProject of this.project.subProjects) {
      if (subProject.id == this.subProjectId) {
        initSubProject = subProject;
        break;
      }
    }
    if (initSubProject == null) {
      this.snackService.showError(
        'Subproject met id ' +
          this.subProjectId +
          ' niet teruggevonden in project'
      );
      return;
    }

    this.project.products[0] = formatFirstProduct(
      this.project.products[0],
      true
    );
    this.project['firstProduct'] = this.project.products[0] as any;

    this.subProject = initSubProject;

    this.placementAssets =
      this.subProject.projectPlacement.projectPlacementAssets
        .filter((a) => a.asset != null)
        .map((a) => a.asset);
    this.ipBoardExamples = [
      this.project.products[0]['ipBoardLeftExample'],
      this.project.products[0]['ipBoardRightExample'],
    ].filter((a) => a != null);
    this.designPictures = this.project.products[0].productAssets
      .filter(
        (a) =>
          [
            'externalDesign',
            'extraDesign',
            'internalDesign',
            'windowDesign',
            'boardDesign',
            'otherDesign',
          ].includes(a.formSection) && a.asset != null
      )
      .map((a) => a.asset);
    this.executionAssets = this.project.products[0].productAssets
      .filter((a) => a.formSection === 'execution' && a.asset != null)
      .map((a) => a.asset);

    this.componentsExecuted = this.project.products.some(
      (p) => p.productPlacement != null && p.productPlacement.executionComplete
    );

    this.selectedCompany = this.clientCompanySelectService.getSelectedCompany();

    this.loading = false;
    return;
  }

  saveChanges(): void {
    this.loading = true;
    this.addSubscription(
      this.requestService.put(this.project, '/projects'),
      (res) => {
        this.loading = false;
        this.snackService.showSuccess(
          'Wijzigingen werden succesvol opgeslagen'
        );
      }
    );
  }

  productInitialized(product: Product, index: number) {
    this.project.products[index] = product;
    this.showAddProduct = this.project.products.every(
      (p) => p.type != null && p.type !== ProductTypes.IP_BOARD.value
    );
    this.projectDisplayObservableService.getProductAddedSubscribable().next();
  }

  addProduct() {
    this.showAddProduct = false;
    let prod = new Product();
    this.project.products.push(prod);
  }
}
